import {
  type AnchorHTMLAttributes,
  forwardRef,
  type MouseEvent,
  type ReactNode,
  useEffect,
} from "react"
import { navigate, useLocation } from "./next-navigation"

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  href: string
  children?: ReactNode
  preserveScroll?: boolean
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { href, onClick, target, children, preserveScroll: _preserveScroll, ...props },
  ref,
) {
  return (
    <a
      {...props}
      href={href}
      target={target}
      ref={ref}
      onClick={(event: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(event)
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          target === "_blank" ||
          href.startsWith("#")
        ) {
          return
        }

        const targetUrl = new URL(href, window.location.href)
        if (targetUrl.origin !== window.location.origin) return
        event.preventDefault()
        navigate(href)
      }}
    >
      {children}
    </a>
  )
})

export function Head({ title, children }: { title?: string; children?: ReactNode }) {
  useEffect(() => {
    if (title) document.title = title
  }, [title])

  return <>{children}</>
}

export function usePage() {
  return { url: useLocation() }
}
